"use client";

import { Card, CardContent, Typography, Box, Grid, Stack } from "@mui/material";
import {
  LocationOn,
  Work,
  School,
  Favorite,
  Group,
  CalendarToday,
  Height,
  Person,
} from "@mui/icons-material";

function ProfileView({ profile }) {
  if (!profile) return <Typography>Loading...</Typography>;

  const safeValue = (value) =>
    value !== null && value !== undefined && value !== "" ? value : "-";

  const InfoRow = ({ label, value }) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "180px 1fr" },
        alignItems: "center",
        py: 0.75,
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, color: "black", whiteSpace: "nowrap" }}
      >
        {label}:
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, color: "black" }}>
        {safeValue(value)}
      </Typography>
    </Box>
  );

  return (
    <Stack spacing={4}>
      {/* Section 1: Profile Photos */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <Person sx={{ color: "black" }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: "black" }}>
              Profile Photos
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {[
              { src: profile.profile_photo, label: "Profile Photo" },
              { src: profile.profile_photo1, label: "Profile Photo 1" },
              { src: profile.profile_photo2, label: "Profile Photo 2" },
              { src: profile.profile_photo3, label: "Profile Photo 3" },
              { src: profile.horoscope, label: "Horoscope" },
              { src: profile.ID_proof, label: "ID Proof" },
            ].map((item, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <Box
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    bgcolor: "white",
                    border: "1px solid #ddd",
                    cursor: "pointer",
                    height: 180,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover img": { transform: "scale(1.05)" },
                  }}
                >
                  <img
                    src={item.src || "/placeholder.svg"}
                    alt={item.label}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transition: "transform 0.3s ease",
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Section 2: Personal Details */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "black" }}>
              {safeValue(profile.name)}
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 3,
                alignItems: "center",
                flexWrap: "wrap",
                color: "black",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarToday sx={{ fontSize: 18 }} />
                <Typography variant="body2">
                  {safeValue(profile.age)} years
                </Typography>
              </Box>
              {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Height sx={{ fontSize: 18 }} />
                <Typography variant="body2">{safeValue(profile.height)}</Typography>
              </Box> */}
            </Box>
          </Box>

          <Grid container spacing={11}>
            <Grid item xs={12} md={6}>
              <Stack>
                <InfoRow label="Religion" value={profile.religion} />
                <InfoRow label="Caste" value={profile.caste} />
                <InfoRow label="Blood Group" value={profile.blood_group} />
                <InfoRow
                  label="Marital Status"
                  value={profile.marital_status}
                />
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack>
                <InfoRow
                  label="Location"
                  value={`${safeValue(profile.city)}, ${safeValue(profile.state)}, ${safeValue(profile.country)}`}
                />
                <InfoRow label="Education" value={profile.education} />
                <InfoRow label="Profession" value={profile.profession} />
                <InfoRow label="Annual Income" value={profile.annual_income} />
              </Stack>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, mb: 1, color: "black" }}
            >
              About Me
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "black", lineHeight: 1.6 }}
            >
              {safeValue(profile.about_me)}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Section 3: Family Details */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <Group sx={{ color: "black" }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: "black" }}>
              Family Details
            </Typography>
          </Box>

          <Grid container spacing={10}>
            <Grid item xs={12} md={6}>
              <Stack>
                <InfoRow label="Father's Name" value={profile.father_name} />
                <InfoRow
                  label="Father's Occupation"
                  value={profile.father_occupation}
                />
                <InfoRow label="Mother's Name" value={profile.mother_name} />
                <InfoRow
                  label="Mother's Occupation"
                  value={profile.mother_occupation}
                />
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack>
                <InfoRow
                  label="Siblings"
                  value={`${profile.no_of_brothers || 0} Brothers, ${profile.no_of_sisters || 0} Sisters`}
                />
                <InfoRow label="Family Type" value={profile.family_type} />
                <InfoRow label="Family Status" value={profile.family_status} />
                <InfoRow label="About Family" value={profile.about_family} />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Section 4: Partner Preferences */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <Favorite sx={{ color: "black" }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: "black" }}>
              Partner Preferences
            </Typography>
          </Box>

          <Grid container spacing={8}>
            <Grid item xs={12} md={6}>
              <Stack>
                <InfoRow label="Religion" value={profile.pref_religion} />
                <InfoRow label="Education" value={profile.pref_education} />
                <InfoRow label="Profession" value={profile.pref_profession} />
                <InfoRow label="Caste" value={profile.pref_caste} />
                <InfoRow label="Blood Group" value={profile.pref_blood_group} />
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack>
                <InfoRow
                  label="Marital Status"
                  value={profile.pref_marital_status}
                />
                <InfoRow
                  label="Annual Income"
                  value={profile.pref_annual_income}
                />
                <InfoRow
                  label="Location"
                  value={`${safeValue(profile.pref_city)}, ${safeValue(profile.pref_state)}, ${safeValue(profile.pref_country)}`}
                />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Stack>
  );
}

export default ProfileView;
